import * as THREE from "three";

export const Slot = {
  none: 0,
  foliage: 1,
  bark: 2,
  litter: 3,
  grass: 4,
  stone: 5,
  soil: 6,
  wood: 7,
  thatch: 8,
  plaster: 9,
  metal: 10,
  water: 11,
  cloth: 12,
  fire: 13,
} as const;

export type SlotName = keyof typeof Slot;

export const SLOT_COUNT = 16;
export const RAMP_STEPS = 64;

const SLOT_NAMES: Array<SlotName | null> = (() => {
  const names: Array<SlotName | null> = new Array(SLOT_COUNT).fill(null);
  for (const [name, index] of Object.entries(Slot)) names[index] = name as SlotName;
  return names;
})();

export interface MonoShift {
  hue: number;
  sat: number;
  light: number;
}

export interface PaletteScheme {
  name: string;
  ramps: Partial<Record<SlotName, number[]>>;
  shifts?: Partial<Record<SlotName, MonoShift[]>>;
  lift?: Partial<Record<SlotName, number>>;
}

export const AUTUMN_FOLIAGE = [
  0xf5d878, 0xf0cb58, 0xe9ba42, 0xe0a633, 0xd6912b, 0xc9761f, 0xb95c1d, 0xa34a1e,
  0x8b3a20,
] as const;

const AUTUMN: PaletteScheme = {
  name: "autumn",
  ramps: {
    none: [0x000000],
    foliage: [...AUTUMN_FOLIAGE],
    bark: [0x6a5340, 0x4a3728],
    litter: [0xe0b055, 0xc08833, 0x94632a],
    grass: [0xa3ab55, 0x7c8a3c, 0x5f6b2c],
    stone: [0x9c9992, 0x6f6c66],
    soil: [0x54402e],
    wood: [0x7c5c3a, 0x5d4229],
    thatch: [0xc4a55c, 0x9c7c3c],
    plaster: [0xd9d3c5],
    metal: [0x6b7077],
    water: [0x376f85, 0x1f4a5e],
    cloth: [0x8c4a4f],
    fire: [0xffb457, 0xff8a2e, 0xff6a1c],
  },
  lift: { litter: 0.4, grass: 0.32, fire: 1 },
};

const MONO: PaletteScheme = {
  name: "mono",
  ramps: {
    fire: [0xffb457, 0xff8a2e, 0xff6a1c],
  },
  lift: { fire: 1 },
};

export const SCHEMES: Record<string, PaletteScheme> = {
  mono: MONO,
  autumn: AUTUMN,
};

function sampleStops<T>(stops: readonly T[], t: number, blend: (a: T, b: T, f: number) => T): T {
  if (stops.length === 1) return stops[0] as T;
  const scaled = t * (stops.length - 1);
  const index = Math.min(stops.length - 2, Math.floor(scaled));
  return blend(stops[index] as T, stops[index + 1] as T, scaled - index);
}

function shifted(base: THREE.Color, stops: readonly MonoShift[], t: number): THREE.Color {
  const shift = sampleStops(stops, t, (a, b, f) => ({
    hue: a.hue + (b.hue - a.hue) * f,
    sat: a.sat + (b.sat - a.sat) * f,
    light: a.light + (b.light - a.light) * f,
  }));

  const hsl = { h: 0, s: 0, l: 0 };
  base.getHSL(hsl);
  const hue = ((hsl.h + shift.hue / 360) % 1 + 1) % 1;
  return new THREE.Color().setHSL(
    hue,
    Math.min(1, hsl.s * shift.sat),
    Math.min(1, hsl.l * shift.light),
  );
}

function colourAt(
  scheme: PaletteScheme,
  name: SlotName | null,
  t: number,
  monoBase: THREE.Color,
): THREE.Color {
  if (name === null) return new THREE.Color(0x000000);

  // explicit ramps win over the mono hue spread, so slots like fire stay warm
  // regardless of what the accent colour is doing
  const stops = scheme.ramps[name];
  if (stops && stops.length > 0) {
    if (stops.length === 1) return new THREE.Color(stops[0] as number);
    const scaled = t * (stops.length - 1);
    const index = Math.min(stops.length - 2, Math.floor(scaled));
    return new THREE.Color(stops[index] as number).lerp(
      new THREE.Color(stops[index + 1] as number),
      scaled - index,
    );
  }

  if (scheme.name === "mono") {
    const monoStops = scheme.shifts?.[name];
    return monoStops ? shifted(monoBase, monoStops, t) : monoBase.clone();
  }

  return new THREE.Color(0x000000);
}

export function buildPalette(
  scheme: PaletteScheme,
  accentColour: number,
  saturation: number,
): THREE.DataTexture {
  const monoBase = new THREE.Color(1, 1, 1).lerp(new THREE.Color(accentColour), saturation);

  const data = new Float32Array(SLOT_COUNT * RAMP_STEPS * 4);
  for (let step = 0; step < RAMP_STEPS; step++) {
    const t = step / (RAMP_STEPS - 1);
    for (let slot = 0; slot < SLOT_COUNT; slot++) {
      const name = SLOT_NAMES[slot] ?? null;
      const colour = colourAt(scheme, name, t, monoBase);
      const offset = (step * SLOT_COUNT + slot) * 4;
      data[offset] = colour.r;
      data[offset + 1] = colour.g;
      data[offset + 2] = colour.b;
      data[offset + 3] = name === null ? 0 : (scheme.lift?.[name] ?? 0);
    }
  }

  const texture = new THREE.DataTexture(data, SLOT_COUNT, RAMP_STEPS, THREE.RGBAFormat);
  texture.type = THREE.FloatType;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}

export function slotNormal(slot: number): number {
  return slot / SLOT_COUNT;
}
